import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import { db } from "@/firebase";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { useAuth as _useAuth } from "./AuthContext";
import { DEMO_PROFILE } from "@/data/demoUser";
import {
  buildSignosFromForm,
  formFieldsFromSignos,
  buildCondicionesArray,
  decomposeCondiciones,
  buildF2Firestore,
  f2ToMem,
  buildF3Firestore,
  f3ToMem,
  buildF4Firestore,
  f4ToMem,
  buildF5Firestore,
  f5ToMem,
  payloadToEnglish,
  payloadToSpanish,
} from "@/data/loinc";
import { getAutoDetectedConditions, calculateIMC } from "@/pages/FormF1/helpers";
import {
  fetchFhirBundle,
  normalizeFhirObservations,
  extractPatientDemographicsFromFhir,
  extractMedicationsFromFhir,
  reconcileProfileWithFhir,
  LOINC_TO_PROFILE_MAP,
} from "@/services/fhirSyncService";
import { linkUserToB2BCompany } from "@/services/b2bInviteService";


// ─── Helpers para Cálculos Clínicos (HOMA-IR, FIB-4) ───────────────────
function calculateAge(birthDateStr) {
  if (!birthDateStr) return 0;
  const birthDate = new Date(birthDateStr);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function calculateHomaIR(glucemia, insulina) {
  const glu = parseFloat(glucemia);
  const ins = parseFloat(insulina);
  if (isNaN(glu) || isNaN(ins) || glu <= 0 || ins <= 0) return "";
  return parseFloat(((glu * ins) / 405).toFixed(2));
}

export function calculateFib4(birthDateStr, ast, alt, plaquetas) {
  const age = calculateAge(birthDateStr);
  const astVal = parseFloat(ast);
  const altVal = parseFloat(alt);
  const plaqVal = parseFloat(plaquetas);
  if (!age || isNaN(astVal) || isNaN(altVal) || isNaN(plaqVal) || astVal <= 0 || altVal <= 0 || plaqVal <= 0) return "";
  const result = (age * astVal) / (plaqVal * Math.sqrt(altVal));
  return parseFloat(result.toFixed(2));
}

const defaultProfile = {
  email: "",
  whatsapp: "",
  nombres: "",
  apellidos: "",
  fechaNacimiento: "",
  sexo: "",
  estadoCivil: "",
  nivelEducativo: "",
  profesion: "",
  ocupacion: "",
  residencia: "",
  peso: "",
  talla: "",
  cintura: "",
  paSistolica: "",
  paDiastolica: "",
  glucemia: "",
  oximetria: "",
  // Gaps de laboratorios y biomarcadores
  egfr: "",
  uacr: "",
  hba1c: "",
  hemoglobin: "",
  cholesterol: "",
  shockIndex: "",
  prq: "",
  hrvRMSSD: "",
  hrvSDNN: "",
  insulina: "",
  homair: "",
  fib4: "",
  ast: "",
  alt: "",
  plaquetas: "",
  condicionIds: [],
  condicionesAdicionales: [],
  tabaco: "",
  alcohol: "",
  sueno: "",
  usaMedicamentos: "",
  medicamentos: [],
  medicationsConfirmedEmpty: false,
  consentimiento: false,
  createdAt: new Date().toISOString(),
  completedAt: null,
  f2Results: null,
  f3Results: null,
  f4Results: null,
  f5Results: null,
  recommendations: null,
};

/** Transforma el perfil en memoria (flat fields) al formato FHIR completo para Firestore */
export function memToFirestore(mem) {
  const {
    peso, talla, cintura, paSistolica, paDiastolica, glucemia, oximetria,
    tabaco, alcohol, sueno, condicionIds, condicionesAdicionales,
    heartRate, heartRateVariability, respirationRate, stressIndex,
    cardiovascularAge, cardiovascularBMI, cvdRisk, wellnessScore,
    ansIndexPNS, ansIndexSNS,
    // Nuevos biomarcadores clínicos de laboratorio y FaceHeart v5.9.3
    egfr, uacr, hba1c, hemoglobin, cholesterol, shockIndex, prq, hrvRMSSD, hrvSDNN,
    insulina, ast, alt, plaquetas,
    nombres, apellidos, fechaNacimiento, sexo, email, whatsapp,
    estadoCivil, nivelEducativo, profesion, ocupacion, residencia,
    usaMedicamentos, medicamentos, consentimiento,
    createdAt, completedAt, vitalsMeasurementDate, vitalsMeasurementMethod,
    f2Results, f3Results, f4Results, f5Results, recommendations,
  } = mem;

  // Cálculos dinámicos en caliente para HOMA-IR y FIB-4
  const computedHoma = mem.homair || calculateHomaIR(glucemia, insulina);
  const computedFib = mem.fib4 || calculateFib4(fechaNacimiento, ast, alt, plaquetas);

  // Build observations array from all vital signs
  const observations = buildSignosFromForm({
    peso, talla, cintura, paSistolica, paDiastolica,
    glucemia, oximetria, tabaco, alcohol, sueno,
    heartRate, heartRateVariability, respirationRate, stressIndex,
    cardiovascularAge, cardiovascularBMI, cvdRisk, wellnessScore,
    ansIndexPNS, ansIndexSNS,
    hemoglobin, cholesterol, shockIndex, prq, hrvRMSSD, hrvSDNN,
    // Inyectar biomarcadores de laboratorio
    egfr, uacr, hba1c, insulina,
    homair: computedHoma,
    fib4: computedFib,
    ast, alt, plaquetas
  }).map((obs) => {
    const fieldKey = LOINC_TO_PROFILE_MAP[obs.code];
    const perMetricDate = fieldKey && mem.fhirFieldTimestamps?.[fieldKey];
    return {
      resourceType: "Observation",
      id: crypto.randomUUID(),
      code: obs.code,
      system: obs.system,
      display: obs.display,
      value: obs.valueCode ? obs.value : obs.value,
      ...(obs.valueCode && { valueCode: obs.valueCode }),
      ...(obs.unit && { unit: obs.unit }),
      date: perMetricDate || vitalsMeasurementDate || completedAt || new Date().toISOString(),
      ...(vitalsMeasurementMethod && { method: vitalsMeasurementMethod }),
    };
  });

  // Build conditions array
  const imc = calculateIMC(peso, talla);
  const autoIds = getAutoDetectedConditions(
    { peso, talla, paSistolica, paDiastolica, glucemia }, imc
  );
  // Condiciones que dependen estrictamente del rango biométrico actual (no deben quedar pegadas si el signo vital vuelve a rango normal)
  const BIOMETRIC_RANGE_CONDITION_IDS = new Set([
    ...(peso && talla ? ["sobrepeso", "obesidad"] : []),
    ...(paSistolica && paDiastolica ? ["pa_elevada"] : []),
    ...(glucemia ? ["hiperglicemia"] : []),
  ]);
  const manualConditionIds = (condicionIds || []).filter(
    (id) => !BIOMETRIC_RANGE_CONDITION_IDS.has(id) || autoIds.includes(id)
  );
  const conditions = buildCondicionesArray(
    manualConditionIds, autoIds, condicionesAdicionales || []
  ).map((cond) => ({
    resourceType: "Condition",
    id: crypto.randomUUID(),
    code: cond.codigoSNOMED || cond.snomedCode,
    system: "http://snomed.info/sct",
    display: cond.nombre || cond.name,
    icd10Code: cond.icd10Code || cond.codigoICD10 || null,
    status: "active",
    date: completedAt || createdAt || new Date().toISOString(),
    ...(cond.source && { category: cond.source }),
  }));

  // Build medications array (preserving both atcCode/code and dosage/dosis)
  const medications = (medicamentos || []).map((med) => {
    const resolvedAtc = med.codigoATC || med.atcCode || med.code || null;
    const resolvedDosage = med.dosis || med.dosificacion || med.dosage || null;
    return {
      resourceType: "MedicationStatement",
      id: crypto.randomUUID(),
      name: med.nombre || med.name,
      atcCode: resolvedAtc,
      code: resolvedAtc,
      dosage: resolvedDosage,
      brandName: med.nombreComercial || med.brandName || null,
      source: med.source || "manual",
      status: "active",
      date: completedAt || createdAt || new Date().toISOString(),
    };
  });

  // Build other array (form results as QuestionnaireResponse)
  const other = [];
  if (f2Results) {
    other.push({
      resourceType: "QuestionnaireResponse",
      questionnaire: "F2-PAR-Q-Physical",
      date: f2Results.completedAt || f2Results.createdAt || completedAt,
      data: buildF2Firestore(f2Results),
    });
  }
  if (f3Results) {
    other.push({
      resourceType: "QuestionnaireResponse",
      questionnaire: "F3-IPAQ",
      date: f3Results.completedAt || f3Results.createdAt || completedAt,
      data: buildF3Firestore(f3Results),
    });
  }
  if (f4Results) {
    other.push({
      resourceType: "QuestionnaireResponse",
      questionnaire: "F4-Nutrition",
      date: f4Results.completedAt || f4Results.createdAt || completedAt,
      data: buildF4Firestore(f4Results),
    });
  }
  if (f5Results) {
    other.push({
      resourceType: "QuestionnaireResponse",
      questionnaire: "F5-Mental-Health",
      date: f5Results.completedAt || f5Results.createdAt || completedAt,
      data: buildF5Firestore(f5Results),
    });
  }

  // FHIR-compliant structure (GCP format)
  const normalizedGender =
    sexo === "Masculino" || sexo === "male"
      ? "male"
      : sexo === "Femenino" || sexo === "female"
      ? "female"
      : sexo
      ? "other"
      : "";

  const firestoreData = {
    resourceType: "Patient",
    id: mem.uid || mem.id, // Firebase UID
    name: `${nombres || ""} ${apellidos || ""}`.trim(),
    nombres: nombres || "",
    apellidos: apellidos || "",
    gender: normalizedGender,
    birthDate: fechaNacimiento,
    email,
    whatsapp,
    maritalStatus: estadoCivil,
    educationLevel: nivelEducativo,
    profession: profesion,
    occupation: ocupacion,
    residence: residencia,
    observations,
    conditions,
    medications,
    usesMedication: usaMedicamentos || (medications.length > 0 ? "Si" : "No"),
    ...(mem.medicationsConfirmedEmpty !== undefined && {
      medicationsConfirmedEmpty: Boolean(mem.medicationsConfirmedEmpty),
    }),
    other,
    createdAt: createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    completedAt,
    consent: consentimiento,
    ...(mem.fhirFieldTimestamps && { fhirFieldTimestamps: mem.fhirFieldTimestamps }),
    ...(recommendations && { recommendations }),
  };

  return firestoreData;
}

/** Transforma el perfil de Firestore (FHIR completo) al formato flat para el frontend */
function firestoreToMem(fs) {
  if (!fs) return { ...defaultProfile };

  // Handle new FHIR format
  if (fs.resourceType === "Patient") {
    const observations = fs.observations || [];
    const conditions = fs.conditions || [];
    const other = fs.other || [];

    // Reconstruct per-metric timestamps from observations and stored fhirFieldTimestamps
    const fhirFieldTimestamps = { ...(fs.fhirFieldTimestamps || {}) };
    observations.forEach((o) => {
      const fieldKey = LOINC_TO_PROFILE_MAP[o.code];
      if (fieldKey && o.date && !fhirFieldTimestamps[fieldKey]) {
        fhirFieldTimestamps[fieldKey] = o.date;
      }
    });

    // Extract name parts (prefer explicit nombres/apellidos if present, otherwise split name safely)
    const rawName = (fs.name || "").trim();
    const nameParts = rawName ? rawName.split(/\s+/) : [];
    const parsedNombres = nameParts.length > 1 ? nameParts.slice(0, -1).join(" ") : (nameParts[0] || "");
    const parsedApellidos = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
    const nombres = fs.nombres || parsedNombres;
    const apellidos = fs.apellidos !== undefined && fs.apellidos !== "" ? fs.apellidos : parsedApellidos;

    // Map gender back (with fallback to fs.sexo if partial update wrote Spanish key)
    const sexo = fs.gender === "male" ? "Masculino" : fs.gender === "female" ? "Femenino" : (fs.sexo || "");

    // Extract vital signs from observations
    const findObs = (code) => observations.find((o) => o.code === code);
    const peso = findObs("29463-7")?.value || "";
    const talla = findObs("8302-2")?.value || "";
    const cintura = findObs("8280-0")?.value || "";
    const paSistolica = findObs("8480-6")?.value || "";
    const paDiastolica = findObs("8462-4")?.value || "";
    const glucemia = findObs("1558-6")?.value || "";
    const oximetria = findObs("2708-6")?.value || "";
    const tabaco = findObs("72166-2")?.value || "";
    const alcohol = findObs("74013-4")?.value || "";
    const sueno = findObs("93832-4")?.value || "";

    // Extract FaceHeart metrics
    const heartRate = findObs("8867-4")?.value || "";
    const heartRateVariability = findObs("80404-7")?.value || "";
    const respirationRate = findObs("9279-1")?.value || "";
    const stressIndex = findObs("we360-stress-index")?.value || "";
    const cardiovascularAge = findObs("we360-cv-age")?.value || "";
    const cardiovascularBMI = findObs("we360-cv-bmi")?.value || "";
    const cvdRisk = findObs("we360-cvd-risk")?.value || "";
    const wellnessScore = findObs("we360-wellness")?.value || "";
    const ansIndexPNS = findObs("we360-ans-pns")?.value || "";
    const ansIndexSNS = findObs("we360-ans-sns")?.value || "";
    const hemoglobin = findObs("718-7")?.value || "";
    const cholesterol = findObs("2093-3")?.value || "";
    const shockIndex = findObs("we360-shock-index")?.value || "";
    const prq = findObs("we360-prq")?.value || "";
    const hrvRMSSD = findObs("we360-hrv-rmssd")?.value || "";
    const hrvSDNN = findObs("we360-hrv-sdnn")?.value || "";

    // Extract laboratory biomarkers (Gaps)
    const egfr = findObs("62238-1")?.value || "";
    const uacr = findObs("13705-9")?.value || "";
    const hba1c = findObs("4548-4")?.value || "";
    const insulina = findObs("15060-7")?.value || "";
    const homair = findObs("98380-2")?.value || "";
    const fib4 = findObs("94344-9")?.value || "";
    const ast = findObs("1920-8")?.value || "";
    const alt = findObs("1742-6")?.value || "";
    const plaquetas = findObs("777-3")?.value || "";

    // Extract measurement metadata
    const vitalObs = observations.find((o) => o.method);
    const vitalsMeasurementDate = vitalObs?.date || null;
    const vitalsMeasurementMethod = vitalObs?.method || null;

    // Extract conditions
    const currentImc = calculateIMC(peso, talla);
    const currentAutoIds = getAutoDetectedConditions(
      { peso, talla, paSistolica, paDiastolica, glucemia }, currentImc
    );
    const biometricRangeIds = new Set([
      ...(peso && talla ? ["sobrepeso", "obesidad"] : []),
      ...(paSistolica && paDiastolica ? ["pa_elevada"] : []),
      ...(glucemia ? ["hiperglicemia"] : []),
    ]);
    const condicionIds = [];
    const condicionesAdicionales = [];
    conditions.forEach((cond) => {
      // Try to match with predefined conditions
      const { condicionIds: ids, condicionesAdicionales: adicionales } = decomposeCondiciones([{
        nombre: cond.display,
        codigoSNOMED: cond.code,
        source: cond.category,
      }]);
      ids.forEach((id) => {
        if (!biometricRangeIds.has(id) || currentAutoIds.includes(id)) {
          if (!condicionIds.includes(id)) condicionIds.push(id);
        }
      });
      condicionesAdicionales.push(...adicionales);
    });

    // Extract form results from other array (and compare timestamp if legacy top-level key also exists)
    const pickNewestForm = (questData, legacyData, toMemFn) => {
      const fromOther = questData ? toMemFn(questData) : null;
      const fromLegacy = legacyData ? toMemFn(legacyData) : null;
      if (fromOther && fromLegacy) {
        const otherTs = fromOther.completedAt || questData?.completedAt || questData?.createdAt || "";
        const legacyTs = fromLegacy.completedAt || legacyData?.completedAt || legacyData?.createdAt || "";
        return legacyTs > otherTs ? fromLegacy : fromOther;
      }
      return fromOther || fromLegacy || null;
    };

    const f2Quest = other.find((q) => q.questionnaire === "F2-PAR-Q-Physical");
    const f3Quest = other.find((q) => q.questionnaire === "F3-IPAQ");
    const f4Quest = other.find((q) => q.questionnaire === "F4-Nutrition");
    const f5Quest = other.find((q) => q.questionnaire === "F5-Mental-Health");

    const f2Results = pickNewestForm(f2Quest?.data, fs.f2Results, f2ToMem);
    const f3Results = pickNewestForm(f3Quest?.data, fs.f3Results, f3ToMem);
    const f4Results = pickNewestForm(f4Quest?.data, fs.f4Results, f4ToMem);
    const f5Results = pickNewestForm(f5Quest?.data, fs.f5Results, f5ToMem);

    return {
      ...defaultProfile,
      uid: fs.id, // Firebase UID
      nombres,
      apellidos,
      fechaNacimiento: fs.birthDate || fs.fechaNacimiento || "",
      sexo,
      email: fs.email || "",
      whatsapp: fs.whatsapp || "",
      estadoCivil: fs.maritalStatus || fs.estadoCivil || "",
      nivelEducativo: fs.educationLevel || fs.nivelEducativo || "",
      profesion: fs.profession || fs.profesion || "",
      ocupacion: fs.occupation || fs.ocupacion || "",
      residencia: fs.residence || fs.residencia || "",
      peso, talla, cintura, paSistolica, paDiastolica, glucemia, oximetria,
      tabaco, alcohol, sueno,
      heartRate, heartRateVariability, respirationRate, stressIndex,
      cardiovascularAge, cardiovascularBMI, cvdRisk, wellnessScore,
      ansIndexPNS, ansIndexSNS,
      hemoglobin, cholesterol, shockIndex, prq, hrvRMSSD, hrvSDNN,
      // Biomarcadores clínicos de laboratorio (Gaps)
      egfr, uacr, hba1c, insulina, homair, fib4, ast, alt, plaquetas,
      vitalsMeasurementDate,
      vitalsMeasurementMethod,
      fhirFieldTimestamps,
      condicionIds,
      condicionesAdicionales,
      usaMedicamentos: ((fs.medications?.length > 0 ? fs.medications : fs.medicamentos) || []).length > 0 ? "Si" : (fs.usesMedication === true ? "Si" : "No"),
      medicationsConfirmedEmpty: fs.medicationsConfirmedEmpty === true,
      medicamentos: ((fs.medications?.length > 0 ? fs.medications : fs.medicamentos) || []).map((med) => {
        const doseVal = med.dosage || med.dosificacion || med.dosis || "";
        return {
          nombre: med.name || med.nombre || "",
          codigoATC: med.atcCode || med.code || med.codigoATC || null,
          dosis: doseVal,
          dosificacion: doseVal,
          nombreComercial: med.brandName || med.nombreComercial || null,
          source: med.source || "manual",
        };
      }),
      consentimiento: fs.consent || false,
      createdAt: fs.createdAt,
      completedAt: fs.completedAt,
      f2Results,
      f3Results,
      f4Results,
      f5Results,
      recommendations: fs.recommendations || null,
    };
  }

  // Handle legacy format (for backwards compatibility during migration)
  const result = payloadToSpanish(fs);

  // F1: signos → flat fields (works with both old Spanish keys and new English keys after reverse)
  if (result.signos) {
    const { signos, condiciones, ...rest } = result;
    const flatFields = formFieldsFromSignos(signos);
    const { condicionIds, condicionesAdicionales } = decomposeCondiciones(condiciones);
    Object.assign(result, rest, flatFields, { condicionIds, condicionesAdicionales });
    delete result.signos;
    delete result.condiciones;
  }

  // F2: observaciones → flat fields
  if (result.f2Results?.observaciones) {
    result.f2Results = f2ToMem(result.f2Results);
  }

  // F3: observaciones → flat fields
  if (result.f3Results?.observaciones) {
    result.f3Results = f3ToMem(result.f3Results);
  }

  // F4: enriched categories → flat keys
  if (result.f4Results) {
    result.f4Results = f4ToMem(result.f4Results);
  }

  // F5: enriched responses → flat indices
  if (result.f5Results) {
    result.f5Results = f5ToMem(result.f5Results);
  }

  return { ...defaultProfile, ...result };
}

const UserProfileContext = createContext(null);

// Hardcoded initial admins
const SEED_ADMIN_EMAILS = [
  "batista.j@latamdigitalmarketing.com",
  "it@saluddigital360.com",
  "itadmin@saluddigital360.com",
];

const isSeedAdminEmail = (email) => {
  if (!email) return false;
  return SEED_ADMIN_EMAILS.some((seed) => seed.toLowerCase() === email.toLowerCase());
};

// Impersonation: read-only "navigate as" for admins
const IMPERSONATE_KEY = "we360.impersonate";

function readImpersonation() {
  try {
    const raw = sessionStorage.getItem(IMPERSONATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * If a pending_assignment exists for this email, return its clientId
 * and remove the pending record. Returns null if no pending assignment.
 */
async function consumePendingAssignment(email) {
  if (!email) return null;
  const key = email.toLowerCase().trim();
  try {
    const ref = doc(db, "pending_assignments", key);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      await deleteDoc(ref);
      return data.clientId || null;
    }
  } catch (err) {
    console.warn("[pending_assignments] lookup failed:", err);
  }
  return null;
}

export function UserProfileProvider({ children }) {
  const { user } = useAuth();
  const [profile, setProfileState] = useState(defaultProfile);
  const profileRef = useRef(defaultProfile);
  const setProfile = useCallback((updaterOrValue) => {
    if (typeof updaterOrValue === "function") {
      const next = updaterOrValue(profileRef.current);
      profileRef.current = next;
      setProfileState(next);
      return next;
    }
    profileRef.current = updaterOrValue;
    setProfileState(updaterOrValue);
    return updaterOrValue;
  }, []);
  const [saving, setSaving] = useState(false);
  const [loadedForUid, setLoadedForUid] = useState(null); // Track which user's profile is loaded
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(readImpersonation);
  const [clientId, setClientId] = useState(null);
  const [clientRole, setClientRole] = useState("member");
  const [isIHO, setIsIHO] = useState(false);
  const [isMedico, setIsMedico] = useState(false);
  const [personalEmail, setPersonalEmail] = useState(null);
  const [personalEmailVerified, setPersonalEmailVerified] = useState(false);
  // Prevents calling check-verified more than once per session
  const verifyCheckedRef = useRef(false);
  const isDemo = user?.isDemo === true;
  // Demo users cannot impersonate — ignore any sessionStorage flag while in demo mode
  const effectiveImpersonation = isDemo ? null : impersonatedUser;
  const isImpersonating = !!effectiveImpersonation;
  const isReadOnly = isDemo || isImpersonating;

  // FHIR Inspector Console state (Admin)
  const [fhirDrawerOpen, setFhirDrawerOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const reloadProfile = useCallback(() => setReloadTick((t) => t + 1), []);
  const openFhirDrawer = useCallback(() => setFhirDrawerOpen(true), []);
  const closeFhirDrawer = useCallback(() => setFhirDrawerOpen(false), []);

  // Effective uid for profile loading: impersonated user wins when active
  const effectiveUid = effectiveImpersonation?.uid || user?.uid || null;

  // Compute loading state: we're loading if we have a user but haven't loaded their profile yet
  const loading = user ? (loadedForUid !== effectiveUid) : false;

  // Load profile from Firestore or Demo data when user / impersonation changes
  useEffect(() => {
    async function loadProfile() {

      if (!user) {
        setProfile(defaultProfile);
        setOnboardingCompleted(false);
        setIsAdmin(false);
        setIsBlocked(false);
        setIsMedico(false);
        setClientId(null);
        setClientRole("member");
        setLoadedForUid(null);
        return;
      }

      // Load demo profile directly — demo always skips onboarding
      if (user.isDemo) {
        setProfile(DEMO_PROFILE);
        setOnboardingCompleted(true);
        setIsAdmin(true);
        setIsBlocked(false);
        setIsMedico(true);
        setIsIHO(true);
        setClientId("demo-enterprise");
        setClientRole("admin");
        setLoadedForUid(user.uid);
        return;
      }

      // ─── Impersonation mode (admin viewing another user's profile) ───
      if (effectiveImpersonation?.uid) {
        try {
          const targetRef = doc(db, "users", effectiveImpersonation.uid);
          const targetSnap = await getDoc(targetRef);
          if (targetSnap.exists()) {
            const data = targetSnap.data();
            // Mark non-admin and non-blocked from the impersonated context — admin keeps real perms via auth
            setIsAdmin(false);
            setIsBlocked(false);
            setIsMedico(data.role === "medico" || data.isMedico === true);
            setClientId(data.clientId || null);
            setClientRole(data.clientRole || "member");
            setOnboardingCompleted(!!data.onboardingCompleted);
            if (data.profile) {
              setProfile({
                ...firestoreToMem(data.profile),
                parqConsentimiento: data.parqConsentimiento || null,
                parqConsentimientoUrl: data.parqConsentimientoUrl || null,
                parqConsentimientoAt: data.parqConsentimientoAt || null,
              });
            } else {
              setProfile({
                ...defaultProfile,
                email: effectiveImpersonation.email || "",
                nombres: effectiveImpersonation.name || "",
              });
            }
          } else {
            setIsMedico(false);
            setProfile({
              ...defaultProfile,
              email: effectiveImpersonation.email || "",
              nombres: effectiveImpersonation.name || "",
            });
          }
        } catch (error) {
          console.error("Error loading impersonated profile:", error);
          setIsMedico(false);
          setProfile(defaultProfile);
        }
        setLoadedForUid(effectiveImpersonation.uid);
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();

          // Check if user is blocked
          if (data.isActive === false) {
            setIsBlocked(true);
            setIsAdmin(false);
            setLoadedForUid(user.uid);
            return;
          }

          // Determine admin role
          const userEmail = (user.email || "").toLowerCase();
          let role = data.role || "user";
          if (window.__E2E_ADMIN__ || isSeedAdminEmail(userEmail)) {
            role = "admin";
          }
          setIsAdmin(role === "admin");
          setIsBlocked(false);

          // Update metadata: role, lastLogin, displayName, email
          const metaUpdate = { lastLogin: new Date().toISOString() };
          if (role !== data.role) metaUpdate.role = role;
          if (user.email && user.email !== data.email) metaUpdate.email = user.email;
          if (user.displayName && user.displayName !== data.displayName) metaUpdate.displayName = user.displayName;

          // Auto-assign clientId from pending_assignments if user has none yet
          let resolvedClientId = data.clientId || null;
          if (!resolvedClientId) {
            const pendingClientId = await consumePendingAssignment(user.email);
            if (pendingClientId) {
              metaUpdate.clientId = pendingClientId;
              resolvedClientId = pendingClientId;
            }
          }

          // Also check if there is a pending B2B invite deep link
          if (!resolvedClientId) {
            const linkedCid = await linkUserToB2BCompany(user.uid, user.email, resolvedClientId);
            if (linkedCid) {
              resolvedClientId = linkedCid;
            }
          }

          await setDoc(userRef, metaUpdate, { merge: true });

          setClientId(resolvedClientId);
          setClientRole(data.clientRole || "member");
          setIsIHO(data.isIHO === true);
          setIsMedico(data.role === "medico" || data.isMedico === true);
          setPersonalEmail(data.personalEmail || null);
          setPersonalEmailVerified(data.personalEmailVerified === true);

          // Sync verification from another device (user not logged in when they clicked the link)
          // verifyCheckedRef prevents repeat calls when user object reference changes at startup
          if (!data.personalEmailVerified && !verifyCheckedRef.current) {
            verifyCheckedRef.current = true;
            const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
            const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
              ? ""
              : RAW_BACKEND_URL;
            fetch(`${BACKEND_URL}/api/email/check-verified?uid=${user.uid}`)
              .then((r) => r.json())
              .then(async (res) => {
                if (res.verified && res.email) {
                  await setDoc(userRef, {
                    personalEmail: res.email,
                    personalEmailVerified: true,
                  }, { merge: true });
                  setPersonalEmail(res.email);
                  setPersonalEmailVerified(true);
                }
              })
              .catch(() => {});
          }

          setOnboardingCompleted(!!data.onboardingCompleted);
          if (data.profile) {
            const parsedProfile = firestoreToMem(data.profile);
            // Mezclar campos PAR-Q, teléfono y estado Outbox del root doc
            const memProfile = {
              ...parsedProfile,
              email: parsedProfile.email || data.email || user.email || "",
              whatsapp: parsedProfile.whatsapp || data.whatsapp || data.telefono || data.phone || "",
              telefono: parsedProfile.telefono || data.telefono || data.whatsapp || data.phone || "",
              fhirId: parsedProfile.fhirId || data.fhirId || null,
              fhirSyncStatus: data.fhirSyncStatus || parsedProfile.fhirSyncStatus || null,
              pendingFhirMetrics: data.pendingFhirMetrics === true,
              pendingFhirF1: data.pendingFhirF1 === true,
              pendingFhirF2: data.pendingFhirF2 === true,
              pendingFhirF3: data.pendingFhirF3 === true,
              pendingFhirF4: data.pendingFhirF4 === true,
              pendingFhirF5: data.pendingFhirF5 === true,
              lastFhirSyncAt: data.lastFhirSyncAt || parsedProfile.lastFhirSyncAt || null,
              parqConsentimiento: data.parqConsentimiento || null,
              parqConsentimientoUrl: data.parqConsentimientoUrl || null,
              parqConsentimientoAt: data.parqConsentimientoAt || null,
            };
            setProfile(memProfile);

            // Reconciliación en dos fases con Google Cloud Healthcare API (FHIR como Fuente de la Verdad):
            // Fase A: Drenar (Flush) cola Outbox local (F1-F5 o mediciones en estado PENDING_FHIR) hacia GCP FHIR.
            // Fase B: Consultar Bundle oficial de FHIR y reconciliar respetando timestamps.
            if (user.email && !isDemo) {
              (async () => {
                let hasUnsyncedLocal = false;

                try {
                  const { syncF1, syncF2, syncF3, syncF4, syncF5 } = await import("@/services/backendSync");

                  // 1. Si quedaron formularios F1..F5 pendientes de subir en el Outbox, subirlos primero
                  if (data.pendingFhirF1 === true) {
                    const f1Res = await syncF1(memProfile, user.email);
                    if (!f1Res?.ok) hasUnsyncedLocal = true;
                  }
                  if (data.pendingFhirF2 === true && memProfile.f2Results) {
                    const f2Res = await syncF2(memProfile.f2Results, user.email);
                    if (!f2Res?.ok) hasUnsyncedLocal = true;
                  }
                  if (data.pendingFhirF3 === true && memProfile.f3Results) {
                    const f3Res = await syncF3(memProfile.f3Results, user.email);
                    if (!f3Res?.ok) hasUnsyncedLocal = true;
                  }
                  if (data.pendingFhirF4 === true && memProfile.f4Results) {
                    const f4Res = await syncF4(memProfile.f4Results, user.email);
                    if (!f4Res?.ok) hasUnsyncedLocal = true;
                  }
                  if (data.pendingFhirF5 === true && memProfile.f5Results) {
                    const f5Res = await syncF5(memProfile.f5Results, user.email);
                    if (!f5Res?.ok) hasUnsyncedLocal = true;
                  }

                  // 2. Drenar cualquier medición con syncStatus === "PENDING_FHIR" en users/{uid}/measurements
                  const { flushPendingMeasurementsToGCP } = await import("@/services/measurements");
                  const flushRes = await flushPendingMeasurementsToGCP(user.uid, user.email);
                  if (!flushRes.allSucceeded) {
                    hasUnsyncedLocal = true;
                  }
                } catch (flushErr) {
                  console.warn("[FHIR Outbox] Error en fase de flush inicial:", flushErr);
                  hasUnsyncedLocal = data.fhirSyncStatus === "PENDING_FHIR";
                }

                // 3. Consultar estado oficial desde Google Cloud Healthcare API (FHIR)
                const bundleRes = await fetchFhirBundle(user.email);

                if (bundleRes.patientFound === false && (data.onboardingCompleted || memProfile.completedAt)) {
                  // Auto-healing: el usuario completó onboarding/F1 pero aún no existe en GCP FHIR
                  try {
                    const { syncF1 } = await import("@/services/backendSync");
                    await syncF1(memProfile, user.email);
                  } catch (syncErr) {
                    console.warn("[FHIR Sync] Auto-healing error:", syncErr);
                  }
                  return;
                }

                if (bundleRes.ok && bundleRes.resources.length > 0) {
                  const latestMemProfile = profileRef.current || memProfile;
                  const normalizedObs = normalizeFhirObservations(bundleRes.resources);
                  const patientDemographics = extractPatientDemographicsFromFhir(bundleRes.resources);
                  const fhirMedications = extractMedicationsFromFhir(bundleRes.resources);
                  const { reconciledProfile, hasUpdates } = reconcileProfileWithFhir(
                    latestMemProfile,
                    normalizedObs,
                    null,
                    { hasPendingLocalChanges: hasUnsyncedLocal, patientDemographics, fhirMedications }
                  );
                  if (hasUpdates) {
                    setProfile((prev) => {
                      const merged = { ...reconciledProfile };
                      if (prev?.completedAt && !merged.completedAt) {
                        merged.completedAt = prev.completedAt;
                      }
                      ["f2Results", "f3Results", "f4Results", "f5Results"].forEach((k) => {
                        const prevTs = prev?.[k]?.completedAt || "";
                        const recTs = merged?.[k]?.completedAt || "";
                        if (prev?.[k] && (!merged?.[k] || prevTs > recTs)) {
                          merged[k] = prev[k];
                        }
                      });
                      setDoc(userRef, {
                        profile: memToFirestore(merged),
                        ...(merged.fhirId && { fhirId: merged.fhirId }),
                        fhirReconciled: true,
                        fhirSyncStatus: merged.fhirSyncStatus || "SYNCED",
                        lastFhirSyncAt: merged.lastFhirSyncAt,
                      }, { merge: true }).catch(() => {});
                      return merged;
                    });
                  }

                  // Auto-healing de datos demográficos: si el perfil local tiene fecha de nacimiento o sexo que aún no están en FHIR
                  const activeProfile = profileRef.current || reconciledProfile || memProfile;
                  const hasMissingDemographicsInFhir =
                    (activeProfile.fechaNacimiento && !patientDemographics?.fechaNacimiento) ||
                    (activeProfile.sexo && !patientDemographics?.sexo);
                  if (hasMissingDemographicsInFhir && !data.pendingFhirF1) {
                    try {
                      await setDoc(
                        userRef,
                        { profile: memToFirestore({ ...activeProfile, uid: user.uid }) },
                        { merge: true }
                      );
                      const { syncF1 } = await import("@/services/backendSync");
                      await syncF1(activeProfile, user.email);
                    } catch (demoSyncErr) {
                      console.warn("[FHIR Sync] Demographic auto-healing error:", demoSyncErr);
                    }
                  }
                }
              })().catch((err) => console.warn("[FHIR Sync] Background error:", err));
            }
          } else {
            const initialProfile = { 
              ...defaultProfile, 
              email: user.email || "",
              whatsapp: data.whatsapp || data.telefono || data.phone || "",
              createdAt: new Date().toISOString() 
            };
            setProfile(initialProfile);

            // Multi-Channel Hydration: si el usuario ya interactuó por WhatsApp Bot u otro canal FHIR antes de llenar su perfil web
            if (user.email && !isDemo) {
              (async () => {
                const bundleRes = await fetchFhirBundle(user.email);
                if (bundleRes.ok && bundleRes.resources.length > 0) {
                  const latestProfile = profileRef.current || initialProfile;
                  const normalizedObs = normalizeFhirObservations(bundleRes.resources);
                  const patientDemographics = extractPatientDemographicsFromFhir(bundleRes.resources);
                  const fhirMedications = extractMedicationsFromFhir(bundleRes.resources);
                  const { reconciledProfile, hasUpdates } = reconcileProfileWithFhir(
                    latestProfile,
                    normalizedObs,
                    null,
                    { hasPendingLocalChanges: false, patientDemographics, fhirMedications }
                  );
                  if (hasUpdates) {
                    setProfile(reconciledProfile);
                    setDoc(userRef, {
                      profile: memToFirestore(reconciledProfile),
                      ...(reconciledProfile.fhirId && { fhirId: reconciledProfile.fhirId }),
                      fhirReconciled: true,
                      fhirSyncStatus: "SYNCED",
                      lastFhirSyncAt: reconciledProfile.lastFhirSyncAt,
                    }, { merge: true }).catch(() => {});
                  }
                }
              })().catch((err) => console.warn("[FHIR Multi-Channel Init] Background error:", err));
            }
          }
        } else {
          // New user — create doc with role and metadata
          const userEmail = (user.email || "").toLowerCase();
          const role = (window.__E2E_ADMIN__ || isSeedAdminEmail(userEmail)) ? "admin" : "user";
          setIsAdmin(role === "admin");
          setIsBlocked(false);

          // Auto-assign from pending_assignments if any
          const pendingClientId = await consumePendingAssignment(user.email);

          await setDoc(userRef, {
            email: user.email || "",
            displayName: user.displayName || "",
            role,
            isActive: true,
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
            ...(pendingClientId && { clientId: pendingClientId }),
          });

          setClientId(pendingClientId || null);
          setClientRole("member");
          setIsIHO(false);
          setIsMedico(false);

          const initialProfile = { 
            ...defaultProfile, 
            email: user.email || "",
            createdAt: new Date().toISOString() 
          };
          setProfile(initialProfile);

          // Multi-Channel Hydration en primer login absoluto: verificar si ya existe en GCP FHIR (ej. creado por Bot de WhatsApp)
          if (user.email && !isDemo) {
            (async () => {
              const bundleRes = await fetchFhirBundle(user.email);
              if (bundleRes.ok && bundleRes.resources.length > 0) {
                const latestProfile = profileRef.current || initialProfile;
                const normalizedObs = normalizeFhirObservations(bundleRes.resources);
                const patientDemographics = extractPatientDemographicsFromFhir(bundleRes.resources);
                const fhirMedications = extractMedicationsFromFhir(bundleRes.resources);
                const { reconciledProfile, hasUpdates } = reconcileProfileWithFhir(
                  latestProfile,
                  normalizedObs,
                  null,
                  { hasPendingLocalChanges: false, patientDemographics, fhirMedications }
                );
                if (hasUpdates) {
                  setProfile(reconciledProfile);
                  setDoc(userRef, {
                    profile: memToFirestore(reconciledProfile),
                    ...(reconciledProfile.fhirId && { fhirId: reconciledProfile.fhirId }),
                    fhirReconciled: true,
                    fhirSyncStatus: "SYNCED",
                    lastFhirSyncAt: reconciledProfile.lastFhirSyncAt,
                  }, { merge: true }).catch(() => {});
                }
              }
            })().catch((err) => console.warn("[FHIR Multi-Channel FirstLogin] Background error:", err));
          }
        }
      } catch (error) {
        console.error("Error loading profile:", error);
        setIsMedico(false);
        setProfile({ ...defaultProfile, email: user?.email || "" });
        setIsAdmin(false);
        setIsBlocked(false);
      }
      setLoadedForUid(user.uid);
    }

    loadProfile();
  }, [user, effectiveImpersonation?.uid, reloadTick]);


  // Save profile to Firestore (not for demo users / not while impersonating)
  const saveProfile = useCallback(async (newProfile) => {
    if (!user || user.isDemo || isImpersonating) return;
    
    setSaving(true);
    try {
      const userRef = doc(db, "users", user.uid);
      // Add uid to profile before converting to FHIR
      const profileWithUid = { ...newProfile, uid: user.uid, email: newProfile.email || user.email || "" };
      const firestoreData = memToFirestore(profileWithUid);
      const outboxFlags = {};
      for (const k of [
        "fhirSyncStatus",
        "fhirSyncedAt",
        "pendingFhirMetrics",
        "pendingFhirF1",
        "pendingFhirF2",
        "pendingFhirF3",
        "pendingFhirF4",
        "pendingFhirF5",
      ]) {
        if (newProfile[k] !== undefined) {
          outboxFlags[k] = newProfile[k];
        }
      }
      await setDoc(userRef, { profile: firestoreData, ...outboxFlags }, { merge: true });
    } catch (error) {
      console.error("Error saving profile:", error);
    }
    setSaving(false);
  }, [user, isImpersonating]);

  const setField = useCallback((key, value) => {
    if (isReadOnly) return; // Don't allow editing in demo or impersonation mode
    
    const updated = setProfile((prev) => ({ ...prev, [key]: value }));
    if (user && !user.isDemo) {
      saveProfile(updated);
    }
  }, [user, saveProfile, isReadOnly, setProfile]);

  const setProfileAndSave = useCallback((newProfileOrUpdater) => {
    // Para usuarios de demostración, permitimos actualizar el estado local en memoria pero evitamos persistir en Firestore
    if (isDemo) {
      setProfile(newProfileOrUpdater);
      return Promise.resolve();
    }
    const targetProfile = setProfile(newProfileOrUpdater);
    if (user && !user.isDemo && !isImpersonating) {
      return saveProfile(targetProfile);
    }
    return Promise.resolve();
  }, [user, saveProfile, isDemo, isImpersonating, setProfile]);

  const toggleCondition = useCallback((condId) => {
    if (isReadOnly) return;
    
    const updated = setProfile((prev) => {
      const ids = prev.condicionIds || [];
      return {
        ...prev,
        condicionIds: ids.includes(condId)
          ? ids.filter((c) => c !== condId)
          : [...ids, condId],
      };
    });
    if (user && !user.isDemo) {
      saveProfile(updated);
    }
  }, [user, saveProfile, isReadOnly, setProfile]);

  const resetProfile = useCallback(async () => {
    if (isReadOnly) return; // Don't allow in demo or impersonation
    
    const fresh = { 
      ...defaultProfile, 
      email: user?.email || "",
      createdAt: new Date().toISOString() 
    };
    setProfile(fresh);
    if (user && !user.isDemo) {
      await saveProfile(fresh);
    }
  }, [user, saveProfile, isReadOnly]);

  const completeOnboarding = useCallback(async () => {
    if (isImpersonating) return;
    setOnboardingCompleted(true);
    if (user && !user.isDemo) {
      try {
        const userRef = doc(db, "users", user.uid);
        await setDoc(userRef, { onboardingCompleted: true }, { merge: true });
      } catch (error) {
        console.error("Error saving onboarding status:", error);
      }
    }
  }, [user, isImpersonating]);

  // ─── Personal email (backup auth) ───────────────────────────
  const updatePersonalEmail = useCallback(async (email) => {
    if (!user || user.isDemo || isImpersonating) return;
    const trimmed = (email || "").trim().toLowerCase();
    const userRef = doc(db, "users", user.uid);
    await setDoc(userRef, {
      personalEmail: trimmed || null,
      personalEmailVerified: false,
    }, { merge: true });
    setPersonalEmail(trimmed || null);
    setPersonalEmailVerified(false);
  }, [user, isImpersonating]);

  // ─── Account deletion (soft delete) ─────────────────────────
  const requestDeletion = useCallback(async () => {
    if (!user || user.isDemo || isImpersonating) return;
    const userRef = doc(db, "users", user.uid);
    await setDoc(userRef, {
      accountStatus: "pending_deletion",
      deletedAt: new Date().toISOString(),
      isActive: false,
    }, { merge: true });
  }, [user, isImpersonating]);

  // ─── Impersonation controls ─────────────────────────────────
  const impersonate = useCallback((target) => {
    // target: { uid, name, email }
    if (!target?.uid) return;
    const payload = {
      uid: target.uid,
      name: target.name || "",
      email: target.email || "",
      startedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(IMPERSONATE_KEY, JSON.stringify(payload));
    setImpersonatedUser(payload);
  }, []);

  const stopImpersonation = useCallback(() => {
    sessionStorage.removeItem(IMPERSONATE_KEY);
    setImpersonatedUser(null);
  }, []);

  const updateProfileFields = useCallback(async (fields) => {
    if (!user || user.isDemo || isReadOnly) return;
    const merged = { ...profile, ...fields };
    setProfile(merged);
    if (user && !user.isDemo) {
      await saveProfile(merged);
    }
  }, [user, profile, isReadOnly, saveProfile]);

  return (
    <UserProfileContext.Provider
      value={{ 
        profile, 
        setField, 
        setProfile: setProfileAndSave, 
        setProfileAndSave,
        toggleCondition, 
        resetProfile,
        completeOnboarding,
        onboardingCompleted,
        loading,
        saving,
        isDemo,
        isAdmin,
        isBlocked,
        // Impersonation
        isImpersonating,
        isReadOnly,
        impersonatedUser: effectiveImpersonation,
        impersonate,
        stopImpersonation,
        // Client role
        clientId,
        clientRole,
        isClientAdmin: (clientRole === "admin" && !!clientId) || isDemo,
        isIHO,
        isMedico,
        // Personal email & account
        personalEmail,
        personalEmailVerified,
        updatePersonalEmail,
        requestDeletion,
        // FHIR Inspector & Reload
        fhirDrawerOpen,
        setFhirDrawerOpen,
        openFhirDrawer,
        closeFhirDrawer,
        reloadProfile,
        updateProfileFields,
      }}
    >
      {children}
    </UserProfileContext.Provider>

  );
}

export function useUserProfile() {
  const context = useContext(UserProfileContext);
  if (!context) throw new Error("useUserProfile must be used within UserProfileProvider");
  return context;
}
